import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, MapPin, Star, MessageCircle, Clock } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ListingCard from "@/components/ListingCard";
import Seo, { personSchema } from "@/components/Seo";

function ResponseTimeChip({ userId }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get(`/users/${userId}/response-stats`)
            .then((r) => setStats(r.data))
            .catch(() => setStats(null));
    }, [userId]);
    if (!stats || stats.avg_response_minutes == null || stats.sample_size < 2) return null;
    const m = stats.avg_response_minutes;
    const label =
        m < 60 ? `${m}m` :
        m < 60 * 24 ? `${Math.round(m / 60)}h` :
        `${Math.round(m / (60 * 24))}d`;
    const rate = stats.response_rate != null ? `${Math.round(stats.response_rate * 100)}%` : null;
    return (
        <div className="mt-2 inline-flex items-center gap-2 text-[11px] text-onyx-500" data-testid="response-time-chip">
            <Clock className="w-3 h-3 text-hellcat" />
            <span>Typically replies in <span className="text-white font-bold">{label}</span></span>
            {rate && <span>· <span className="text-white">{rate}</span> reply rate</span>}
        </div>
    );
}

export default function UserProfile() {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [listings, setListings] = useState([]);
    const { user: me } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/users/${id}`);
                setUser(data);
            } catch {}
            try {
                const { data } = await api.get(`/users/${id}/vehicles`);
                setVehicles(data);
            } catch {}
            try {
                const { data } = await api.get(`/listings`, { params: { limit: 40 } });
                setListings(data.items.filter((l) => l.owner_id === id));
            } catch {}
        })();
    }, [id]);

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-onyx-900">
                <div className="w-10 h-10 border-2 border-onyx-600 border-t-hellcat rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title={`${user.name} — SRT Finder Seller Profile${user.verified_seller ? " (Verified)" : ""}`}
                description={
                    user.bio ||
                    `${user.name} is a ${user.verified_seller ? "verified " : ""}SRT Finder seller${user.location ? ` based in ${user.location}` : ""}. See their garage, active Hellcat, SRT, R/T, TRX and Scat Pack listings, and seller rating history.`
                }
                canonicalPath={`/users/${user.user_id}`}
                image={user.avatar_url}
                imageAlt={`${user.name} — Mopar seller on SRT Finder`}
                jsonLd={personSchema(user)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: user.name, path: `/users/${user.user_id}` },
                ]}
            />
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg truncate">{user.name}</div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 py-6">
                <div className="surface rounded-md p-5 flex items-start gap-4">
                    <div className="w-16 h-16 rounded-md overflow-hidden surface-elevated shrink-0">
                        {user.avatar_url ? (
                            <img
                                src={absUrl(user.avatar_url)}
                                className="w-full h-full object-cover"
                                alt=""
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center headline text-2xl text-onyx-500">
                                {user.name?.[0]}
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="headline text-2xl truncate">{user.name}</h1>
                            {user.verified_seller && (
                                <BadgeCheck className="w-5 h-5 text-hellcat" />
                            )}
                        </div>
                        <div className="text-xs text-onyx-500 flex items-center gap-3 mt-1 flex-wrap">
                            {user.location && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {user.location}
                                </span>
                            )}
                            {user.seller_rating > 0 && (
                                <span className="flex items-center gap-1">
                                    <Star className="w-3 h-3 fill-hellcat text-hellcat" />
                                    {user.seller_rating.toFixed(1)} · {user.seller_rating_count} sales
                                </span>
                            )}
                            <span>{user.community_reputation} rep</span>
                        </div>
                        {user.bio && <div className="text-sm text-onyx-300 mt-2">{user.bio}</div>}
                        <ResponseTimeChip userId={user.user_id} />
                    </div>
                    {me && me.user_id !== user.user_id && (
                        <button
                            onClick={() =>
                                navigate(`/messages`, {
                                    state: { newTo: user.user_id },
                                })
                            }
                            className="btn-red rounded-sm px-3 py-2 text-xs inline-flex items-center gap-1"
                            data-testid="profile-message-btn"
                        >
                            <MessageCircle className="w-4 h-4" /> Message
                        </button>
                    )}
                </div>

                {vehicles.length > 0 && (
                    <section className="mt-6">
                        <div className="label-caps mb-3">Garage</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {vehicles.map((v) => (
                                <div key={v.vehicle_id} className="surface rounded-md overflow-hidden">
                                    <div className="aspect-[16/10] listing-image">
                                        {v.photos?.[0] ? (
                                            <img
                                                src={absUrl(v.photos[0])}
                                                className="w-full h-full object-cover"
                                                alt=""
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-onyx-500 label-caps">
                                                No Photo
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="headline text-lg">
                                            {v.year} {v.model_name}
                                        </div>
                                        <div className="text-sm text-onyx-500">{v.trim}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {listings.length > 0 && (
                    <section className="mt-6">
                        <div className="label-caps mb-3">Active listings</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {listings.map((l) => (
                                <ListingCard listing={l} key={l.listing_id} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
